import React,  {useState} from "react";
import {useSelector, useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";
import {Badge} from "react-bootstrap";
import classNames from "class-names";
import {Link} from "react-router-dom";
import {ChevronRight, MapPin, UserCheck, Users} from "react-feather";

import * as actions from '../../store/actions'

import CircleCheck from "../CircleCheck/CircleCheck";

import Icon from "../Icon/Icon";

import Styles from './Service.module.scss';
import { formatPrice } from "../../helpers/function/format";

const Service = (props) => {
    const {
        service,
        type,
        indent = false,
        showCheckIcon = true,
        to
    } = props;

    const {
        id,
        name,
        showSessionLeft,
        available,
        total,
        subName,
        price,
        length, 
        quantity,
        closed,
        booked,
        cancelled,
        bought,
        employeeName,
        street,
        city,
        zip,
        isHidePrice,
    } = service ?? {};

    const tintColor = useSelector(state => state.companies.tintColor);
    const selectedServiceID = useSelector(state => state.booking.selectedServiceID);
    const selectedGroupID = useSelector(state => state.groups.selectedGroupID);
    const selectedEventID = useSelector(state => state.events.selectedEventID);
    const dispatch = useDispatch();

    const [isHovered, setHovered] = useState(false);
    const {t} = useTranslation('common');

    let checked;
    switch (type) {
        case 'booking':
            checked = selectedServiceID === id;
            break;
        case 'group':
            checked = selectedGroupID === id;
            break;
        case 'event':
            checked = selectedEventID === id;
            break;
        default:
            checked = false;
            break;
    }

    const isShowPrice = !(isHidePrice === true && Number(price) === 0);

    const content = (
        <div className={Styles.detailsContainer} style={{paddingLeft: indent ? 16 : 0}}>
            <span className={Styles.name} data-testid='data-test-name-container'>
                <span data-testid='data-test-name'>{name}</span>

                {closed && (
                    <Badge
                        variant={'danger'}
                        className={'ml-3 text-uppercase'}
                        data-testid={'data-test-service-full'}
                    >
                        {t('homeScreen.events.full')}
                    </Badge>
                )}

                {booked && (
                    <Badge
                        variant={'success'}
                        className={'ml-3 text-uppercase'}
                        data-testid={'data-test-service-booked'}
                    >
                        {t('homeScreen.events.booked')}
                    </Badge>
                )}

                {cancelled && (
                    <Badge
                        variant={'danger'}
                        className={'ml-3 text-uppercase'}
                        data-testid={'data-test-service-cancelled'}
                    >
                        {t('homeScreen.events.cancelled')}
                    </Badge>
                )}

                {bought && (
                    <Badge
                        variant={'success'}
                        className={'ml-3 text-uppercase'}
                        data-testid={'data-test-service-bought'}
                    >
                        {t('homeScreen.bundles.bought')}
                    </Badge>
                )}
            </span>

            {subName && (
                <span className={Styles.subTitle} data-testid='data-test-subtitle'>
                    {subName}
                </span>
            )}

            <div className={Styles.details}>
                {isShowPrice && (
                    <span className={Styles.detail}>
                        <div>
                            <Icon
                                name={'credit-card'}
                                size={18}
                                className={Styles.icon}
                                color={isHovered || checked 
                                    ? tintColor
                                    : undefined
                                }
                            />
                        </div>

                        <span className={Styles.value} data-testid='data-test-price'>
                          {formatPrice(price)}
                        </span>
                    </span>
                )}

                {quantity && (
                    <span className={Styles.detail}>
                        <div>
                            <Icon
                                name={'box'}
                                size={18}
                                className={Styles.icon}
                                color={isHovered || checked
                                    ? tintColor
                                    : undefined
                                }
                            />
                        </div>
                        <span className={Styles.value} data-testid='data-test-quantity'>
                            {quantity}
                        </span>
                    </span>
                )}

                <span className={Styles.detail}>
                    <div>
                        <Icon
                            name={'clock'}
                            size={18}
                            className={Styles.icon}
                            color={isHovered || checked
                                ? tintColor
                                : undefined
                            }
                        />
                    </div>
                    <span className={Styles.value} data-testid='data-test-length'>
                        {length}
                    </span>
                </span>

                {Boolean(available && total && showSessionLeft) && (
                    <div className={Styles.detail}>
                            <div>
                                <Users
                                    size={18}
                                    color={isHovered
                                        ? tintColor
                                        : undefined
                                    }
                                />
                            </div>
                            <span className={Styles.value} data-testid='data-test-available'>
                                {`${total-available}/${total}`}
                            </span>
                    </div>
                )}

                {Boolean(employeeName) && (
                    <div className={Styles.teacher}>
                        <div>
                            <UserCheck
                                size={18}
                                color={isHovered
                                    ? tintColor
                                    : undefined
                                }
                            />
                        </div>
                        <span className={Styles.value} data-testid='data-test-employee'>
                            {employeeName}
                        </span>
                    </div>
                )}

                {Boolean(street && city && zip) && (
                    <div className={Styles.address}>
                        <div>
                            <MapPin
                                size={18}
                                color={isHovered
                                    ? tintColor
                                    : undefined
                                }
                            />
                        </div>
                        <span className={Styles.value} data-testid='data-test-address'>
                            {`${street}, ${zip}, ${city}`}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );

    if (to) {
        return (
            <Link
                className={classNames(Styles.container, Styles[type])}
                style={{ pointerEvents: (cancelled || closed) ?  'none' : ''}}
                to={to}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                data-testid={`data-test-service-button-${name}`}
            >
                {content}

                <ChevronRight
                    className={Styles.chevron}
                    color={isHovered ? tintColor : undefined}
                />
            </Link>
        )
    }

    if (!showCheckIcon) {
        return (
            <div className={classNames(Styles.container, Styles[type])} data-testid={`data-test-service-button-${name}`}>
                {content}
            </div>
        );
    }

    return (
        <button
            disabled={cancelled || closed}    
            className={classNames(Styles.container, Styles[type])}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={() => {
                switch (type) {
                    case 'booking':
                        dispatch(actions.selectService(selectedServiceID === id ? null : id));
                        break;
                    case 'group':
                        dispatch(actions.selectGroup(selectedGroupID === id ? null : id));
                        break;
                    case 'event':
                        dispatch(actions.selectEvent(selectedEventID === id ? null : id));
                        break;
                    default:
                        break;
                }
            }}
            data-testid={`data-test-service-button-${name}`}
        >
            {content}

            <CircleCheck
                color={isHovered || checked
                    ? tintColor
                    : undefined
                }
                checked={checked}
            />
        </button>
    )
};

export default Service